import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import _ from "lodash";
import Select from "components/common/select/Select.component";
import { FormField } from "components/common/FormField.component";
import translate from "components/translations/translations.wrapper.jsx";
import type { ISelectOption } from "types/gaiaComponents/select";
import type {
    AdvancedSearchInputHandle,
    AdvancedSearchInputParams,
    AdvancedSearchInputProps,
    AdvancedSearchInputResponse,
    AdvancedSearchInputResponseItem
} from "types/advancedSearchInput";

/*
* https://github.com/phunware/react-select because react-select doesn't trigger onMenuScrollToBottom
* */

const COMMON_PARAMS: AdvancedSearchInputParams = {
    prefix: "",
    page: 1,
    rows: 20,
    usedInIndexOnly: true,
    showInactive: true,
};

const toFirstPageOption = (item: AdvancedSearchInputResponseItem): ISelectOption => ({
    label: (item.number > 0 || typeof item.number === "string" ? (item.number + " - ") : "") + item.name,
    value: item.number,
    color: (item.active) ? "black" : "lightgrey",
});

const toNextPageOption = (item: AdvancedSearchInputResponseItem): ISelectOption => ({
    label: "[" + item.number + "] " + item.name,
    value: item.number,
    color: (item.active) ? "black" : "lightgrey",
});

export const AdvancedSearchInput = forwardRef<AdvancedSearchInputHandle, AdvancedSearchInputProps>(function AdvancedSearchInput(props, ref) {
    const [options, setOptions] = useState<ISelectOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Paging and the debounced search run from callbacks created in earlier renders,
    // so everything they read is kept in refs to always see the latest values
    const selectRef = useRef<{ focus: () => void }>(null);
    // the prefix the shown options were loaded with; the typed text only applies once its debounced search answers
    const loadedPrefixRef = useRef(COMMON_PARAMS.prefix);
    const optionsCountRef = useRef(0);
    const currentPageRef = useRef(COMMON_PARAMS.page);
    const recordsRef = useRef(1);
    // only the response of the latest first-page request may replace the options
    const requestIdRef = useRef(0);
    const isFirstPageLoadingRef = useRef(false);
    const isNextPageLoadingRef = useRef(false);
    const isMountedRef = useRef(true);

    useImperativeHandle(ref, () => ({
        focus: () => selectRef.current?.focus(),
    }), []);

    const getParams = (page: number, prefix: string): AdvancedSearchInputParams => ({
        ...COMMON_PARAMS,
        usedInIndexOnly: props.usedInIndexOnly === false ? false : COMMON_PARAMS.usedInIndexOnly,
        prefix,
        page,
    });

    const loadFirstPage = (prefix: string) => {
        setOptions([]);
        optionsCountRef.current = 0;
        setIsLoading(true);

        if (typeof props.dimension === "undefined")
            return;

        const requestId = ++requestIdRef.current;
        isFirstPageLoadingRef.current = true;
        // a next page still in flight belongs to the old list, so it must not hold back paging the new one
        isNextPageLoadingRef.current = false;

        props.type(getParams(COMMON_PARAMS.page, prefix), props.dimension, props.companyId).then((response: AdvancedSearchInputResponse) => {
            if (!isMountedRef.current || requestId !== requestIdRef.current)
                return;

            let values = response.rows;
            if (values && values.length > 0) {
                const specialValues: AdvancedSearchInputResponseItem[] = [];
                if (props.hasUnassigned)
                    specialValues.push({ number: -1, name: props.translate("generic.unassigned"), active: true });
                if (props.hasAny)
                    specialValues.push({ number: -99, name: props.translate("generic.genericAny"), active: true });
                values = specialValues.concat(values);
            }

            const results = values.map(toFirstPageOption);
            optionsCountRef.current = results.length;
            loadedPrefixRef.current = prefix;
            currentPageRef.current = response.page;
            recordsRef.current = response.records;
            setOptions(results);
            setIsLoading(false);
        }).finally(() => {
            if (requestId === requestIdRef.current)
                isFirstPageLoadingRef.current = false;
        });
    };

    const scroll = () => {
        if (isFirstPageLoadingRef.current || isNextPageLoadingRef.current || recordsRef.current <= optionsCountRef.current)
            return undefined;

        const requestId = requestIdRef.current;
        isNextPageLoadingRef.current = true;

        return props.type(getParams(currentPageRef.current + 1, loadedPrefixRef.current), props.dimension, props.companyId).then((response: AdvancedSearchInputResponse) => {
            // a newer first-page request has replaced the list this page belonged to
            if (!isMountedRef.current || requestId !== requestIdRef.current)
                return;

            const results = response.rows.map(toNextPageOption);
            optionsCountRef.current += results.length;
            currentPageRef.current = response.page;
            recordsRef.current = response.records;
            setOptions(previousOptions => previousOptions.concat(results));
        }).finally(() => {
            if (requestId === requestIdRef.current)
                isNextPageLoadingRef.current = false;
        });
    };

    // the debounced search is created once, so it calls the latest search logic through a ref
    const searchTypedTextRef = useRef<(text: string) => void>(() => undefined);
    useEffect(() => {
        searchTypedTextRef.current = (text: string) => {
            if (recordsRef.current > 20)
                loadFirstPage(text);
        };
    });
    const [searchWithDelay] = useState(() => _.debounce((text: string) => searchTypedTextRef.current(text), 1000));

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            searchWithDelay.cancel();
        };
    }, [searchWithDelay]);

    const onInputChange = (newValue: string) => {
        searchWithDelay(newValue);
    };

    return (
        <div className={"col-md-3"}>
            <div className="col-md-12 p-0 row m-0">
                    <FormField
                        id={"search" + props.name}
                        label={props.name}
                        disabled={props.disabled}
                    >
                        <Select
                            isClearable={true}
                            ref={selectRef}
                            isDisabled={props.disabled}
                            onBlurResetsInput={false}
                            onCloseResetsInput={false}
                            isSearchable={true}
                            isLoading={isLoading}
                            value={props.selectedValue}
                            placeholder={props.placeholder}
                            menuPlacement={(props.menuPlacement as 'auto' | 'top' | 'bottom') || "bottom"}
                            menuPosition={props.menuPosition === "fixed" ? "fixed" : "absolute"}
                            tabIndex={props.tabIndex}
                            autoFocus={props.autoFocus}
                            options={options}
                            tabSelectsValue={props.tabSelectsValue ? props.tabSelectsValue : false}
                            onMenuOpen={() => loadFirstPage("")}
                            onInputChange={onInputChange}
                            onChange={props.onChange}
                            onMenuScrollToBottom={scroll}
                            closeMenuOnSelect={true}
                            translate={props.translate}
                        />
                    </FormField>
            </div>
        </div>
    );
});

const withTranslations = translate(AdvancedSearchInput);
export default withTranslations;
